import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { StreamStatus, UploadFileInfo } from '../common/api.types';
import { sseStream } from '../common/sse';
import {
  AnalysisContrast, AnalysisOverview, ContrastFindings, DmrRegion, LiteratureGene, PathwayTerm,
  IngestionSummary, MatrixOverview, MatrixOverviewQuery, MatrixPreview, OmicsChart,
  OmicsEdaVariable, OmicsRunConfig, OmicsService, OmicsSession, ProcessingSummary,
  StrategyConfirm, StrategyReview,
} from './omics.service';

const SESSION_KEY = 'aha_omics_session_id';

/** The overview as it travels: the bytes base64-encoded inside the JSON, so the dimensions
 *  and the pixels arrive together in one request. */
interface MatrixOverviewWire extends Omit<MatrixOverview, 'values'> {
  values_b64: string;
}

/** Real implementation against `/aha/v1/omics/…`. Its own session key: the
 *  omics API has its own session store, so reusing tabular's would hand an
 *  omics endpoint a session id it has never heard of. */
@Injectable()
export class OmicsRealService extends OmicsService {
  private readonly http = inject(HttpClient);
  private readonly api = environment.apiUrl;
  private runName = '';
  // Every stage endpoint is /omics/{modality}/…; the run remembers it, so the
  // segment is carried here rather than threaded through each call.
  private modality: OmicsRunConfig['modality'] = 'meth';

  constructor() {
    super();
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) this.sessionId = stored;
    }
  }

  override setRunName(name: string): void { this.runName = name; }

  override setSession(id: string): void {
    this.sessionId = id;
    if (typeof localStorage !== 'undefined') localStorage.setItem(SESSION_KEY, id);
  }

  private get resolvedSessionId(): string {
    if (!this.sessionId && typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) this.sessionId = stored;
    }
    return this.sessionId;
  }

  // GET /aha/v1/omics/upload/files
  listFiles(): Observable<UploadFileInfo[]> {
    return this.http.get<{ files: UploadFileInfo[] }>(`${this.api}/omics/upload/files`)
      .pipe(map(res => res.files));
  }

  // POST /aha/v1/omics/sessions                     → session_id
  // POST /aha/v1/omics/{modality}/ingestion/start   → launches job 1
  // GET  /aha/v1/omics/{modality}/ingestion/status  (SSE)
  //
  // ingestion/start is the only endpoint that treats the modality path segment
  // as authoritative — it writes it onto the run — so it has to match what the
  // session was created with.
  startIngestion(config: OmicsRunConfig): Observable<StreamStatus> {
    const launch = (sid: string, observer: any) => {
      this.http.post<{ ok: boolean; launched: boolean }>(
        `${this.api}/omics/${config.modality}/ingestion/start`, { session_id: sid },
      ).subscribe({
        next: () => sseStream(
          `${this.api}/omics/${config.modality}/ingestion/status?session_id=${sid}`,
        ).subscribe(observer),
        error: err => observer.error(err),
      });
    };

    return new Observable(observer => {
      this.modality = config.modality;
      const sid = this.resolvedSessionId;
      if (sid) { launch(sid, observer); return; }

      this.http.post<{ ok: boolean; session_id: string }>(`${this.api}/omics/sessions`, {
        modality: config.modality,
        name: this.runName,
        config: { data_dir: config.data_dir, csv_paths: config.csv_paths },
      }).subscribe({
        next: res => { this.setSession(res.session_id); launch(res.session_id, observer); },
        error: err => observer.error(err),
      });
    });
  }

  reconnectIngestion(config: OmicsRunConfig): Observable<StreamStatus> {
    return sseStream(
      `${this.api}/omics/${config.modality}/ingestion/status?session_id=${this.resolvedSessionId}`,
    );
  }

  // GET /aha/v1/omics/{modality}/ingestion/summary
  getIngestionSummary(): Observable<IngestionSummary> {
    return this.http.get<{ summary: IngestionSummary }>(
      `${this.api}/omics/${this.modality}/ingestion/summary`,
      { params: { session_id: this.resolvedSessionId } },
    ).pipe(map(res => res.summary));
  }

  // GET /aha/v1/omics/sessions
  getSessions(): Observable<OmicsSession[]> {
    return this.http.get<{ sessions: OmicsSession[] }>(`${this.api}/omics/sessions`)
      .pipe(map(res => res.sessions));
  }

  // POST /aha/v1/omics/{modality}/strategy/start  → { ok, launched: false }
  // GET  /aha/v1/omics/{modality}/strategy/status (SSE)
  //
  // `launched: false` is expected: job 1, started by ingestion/start, already drives this
  // stage. The POST is an acknowledgement; the stream is the real work, and it ends at the
  // review gate with "Parameters ready for review."
  startStrategy(): Observable<StreamStatus> {
    return new Observable(observer => {
      this.http.post<{ ok: boolean; launched: boolean }>(
        `${this.api}/omics/${this.modality}/strategy/start`, { session_id: this.resolvedSessionId },
      ).subscribe({
        next: () => this.reconnectStrategy().subscribe(observer),
        error: err => observer.error(err),
      });
    });
  }

  reconnectStrategy(): Observable<StreamStatus> {
    return sseStream(
      `${this.api}/omics/${this.modality}/strategy/status?session_id=${this.resolvedSessionId}`,
    );
  }

  // GET /aha/v1/omics/{modality}/strategy/review
  getStrategyReview(): Observable<StrategyReview> {
    return this.http.get<{ review: StrategyReview }>(
      `${this.api}/omics/${this.modality}/strategy/review?session_id=${this.resolvedSessionId}`,
    ).pipe(map(res => res.review));
  }

  // POST /aha/v1/omics/{modality}/strategy/charts
  //
  // A POST rather than a GET: the selection is the input, and it is a list of objects with
  // their own value maps — too much to put in a query string. Nothing is mutated beyond the
  // chart payloads, which the pipeline rewrites from result files it never touches.
  rebuildCharts(variables: OmicsEdaVariable[]): Observable<OmicsChart[]> {
    return this.http.post<{ charts: OmicsChart[] }>(
      `${this.api}/omics/${this.modality}/strategy/charts`,
      { session_id: this.resolvedSessionId, eda_variables: variables },
    ).pipe(map(res => res.charts));
  }

  // POST /aha/v1/omics/{modality}/processing/start  → confirms the gate, launches job 2
  // GET  /aha/v1/omics/{modality}/processing/status (SSE)
  startProcessing(review: StrategyConfirm): Observable<StreamStatus> {
    return new Observable(observer => {
      this.http.post<{ ok: boolean }>(
        `${this.api}/omics/${this.modality}/processing/start`,
        { session_id: this.resolvedSessionId, ...review },
      ).subscribe({
        next: () => this.reconnectProcessing().subscribe(observer),
        error: err => observer.error(err),
      });
    });
  }

  reconnectProcessing(): Observable<StreamStatus> {
    return sseStream(
      `${this.api}/omics/${this.modality}/processing/status?session_id=${this.resolvedSessionId}`,
    );
  }

  // POST /aha/v1/omics/{modality}/analysis/start → then SSE /analysis/status
  startAnalysis(): Observable<StreamStatus> {
    return new Observable(observer => {
      this.http.post<{ ok: boolean }>(
        `${this.api}/omics/${this.modality}/analysis/start`,
        { session_id: this.resolvedSessionId },
      ).subscribe({
        next: () => this.reconnectAnalysis().subscribe(observer),
        error: err => observer.error(err),
      });
    });
  }

  reconnectAnalysis(): Observable<StreamStatus> {
    return sseStream(`${this.api}/omics/${this.modality}/analysis/status?session_id=${this.resolvedSessionId}`);
  }

  // ── Stage 4: downstream analysis ─────────────────────────────────────────
  //
  // There is no single "give me the analysis screen" endpoint, and there does not need to
  // be: the stage's own outputs are already catalogued. `analysis/results` carries the
  // warnings and the batch-adjustment decision, and every table on the page is one
  // allowlisted CSV read back with `format=json`, which the API parses server-side so the
  // browser never carries a CSV parser. Names come from the documented per-contrast
  // families (`qc_genomic_inflation`, `literature_review_`, `dmr_results_`, `go_enrichment_`).

  /** A 404 here means "this run did not produce that file", which for a per-contrast family
   *  is a real answer — the pipeline skips a section rather than emitting an empty one — so
   *  it resolves to null instead of failing the whole screen. */
  private artifact<T>(name: string): Observable<{ rows: T[] } | null> {
    return this.http.get<{ rows: T[] }>(
      `${this.api}/omics/${this.modality}/artifacts`,
      { params: { session_id: this.resolvedSessionId, name, format: 'json' } },
    ).pipe(catchError(() => of(null)));
  }

  getAnalysisOverview(): Observable<AnalysisOverview> {
    type Lam = {
      Contrast: string; Lambda: number | null; Lambda_Verdict: string;
      N_Group1: number; N_Group2: number;
      N_FDR_Sig_PerContrast: number; N_Probes_Tested: number;
    };
    type Warn = { kind: string; detail: string };
    // The stage records the adjustment it tried and both lambdas it measured, keyed by
    // contrast. `kept: false` means it was REVERTED and the reported statistics are the
    // un-adjusted ones — a very different result from a run that never attempted one.
    type Batch = {
      kept: boolean;
      lambda_before?: Record<string, number>;
      lambda_after?: Record<string, number>;
    };

    return forkJoin({
      results: this.http.get<{ results: { warnings?: Warn[]; batch_adjustment?: Batch[] } }>(
        `${this.api}/omics/${this.modality}/analysis/results`,
        { params: { session_id: this.resolvedSessionId } },
      ),
      lambda: this.artifact<Lam>('qc_genomic_inflation.csv'),
    }).pipe(map(({ results, lambda }) => {
      const rows = lambda?.rows ?? [];
      // Only a kept adjustment has a meaningful "before": on a reverted one the two lambdas
      // describe an experiment whose result was thrown away.
      const kept = (results.results?.batch_adjustment ?? []).find(b => b.kept);
      const contrasts: AnalysisContrast[] = rows.map(r => ({
        id: r.Contrast,
        // Until the run reports the display labels it agreed with the user in Strategy &
        // EDA, the pipeline's own name is the honest thing to show — inventing a prettier
        // one here would name a comparison the run never called that.
        label: r.Contrast,
        n_significant: r.N_FDR_Sig_PerContrast ?? 0,
        lambda_before: kept?.lambda_before?.[r.Contrast] ?? null,
        lambda_after: kept?.lambda_after?.[r.Contrast] ?? r.Lambda,
        verdict: (r.Lambda_Verdict as AnalysisContrast['verdict']) ?? 'not_computed',
        n: [Math.min(r.N_Group1, r.N_Group2), Math.max(r.N_Group1, r.N_Group2)],
      }));
      return {
        probes_tested: rows[0]?.N_Probes_Tested ?? 0,
        findings: (results.results?.warnings ?? []).map(w => ({
          title: w.kind.replace(/_/g, ' '),
          detail: w.detail,
          verdict: 'moderate' as const,
        })),
        contrasts,
      };
    }));
  }

  getContrastFindings(contrastId: string): Observable<ContrastFindings> {
    type LitRow = {
      Gene: string; Probe: string; Methylation_Pattern: string; Delta_Beta: number;
      Cohens_D: number; N_Probes: number; Gene_Function: string; Known_Pathways: string;
      Literature_Summary: string; Novelty_Assessment: string; Key_Sources: string;
    };
    type DmrRow = {
      seqnames: string; start: number; end: number; 'no.cpgs': number;
      'overlapping.genes': string; meandiff: number;
    };
    type GoRow = { GOID: string; TERM: string; DE: number; FDR: number };

    return forkJoin({
      lit: this.artifact<LitRow>(`literature_review_${contrastId}.csv`),
      dmr: this.artifact<DmrRow>(`dmr_results_${contrastId}.csv`),
      go: this.artifact<GoRow>(`go_enrichment_${contrastId}.csv`),
    }).pipe(map(({ lit, dmr, go }) => {
      const genes: LiteratureGene[] = (lit?.rows ?? []).map(r => ({
        gene: r.Gene, probe: r.Probe, pattern: r.Methylation_Pattern,
        delta_beta: r.Delta_Beta, cohens_d: r.Cohens_D, n_probes: r.N_Probes,
        novelty: r.Novelty_Assessment, function: r.Gene_Function,
        pathways: r.Known_Pathways, summary: r.Literature_Summary, sources: r.Key_Sources,
      }));
      const regions: DmrRegion[] = (dmr?.rows ?? []).map(r => ({
        chr: r.seqnames, start: r.start, end: r.end, cpgs: r['no.cpgs'],
        genes: r['overlapping.genes'], mean_diff: r.meandiff,
      }));
      const pathways: PathwayTerm[] = (go?.rows ?? [])
        .filter(r => r.FDR < 0.05)
        .sort((a, b) => a.FDR - b.FDR)
        .map(r => ({ term: r.GOID, name: r.TERM, genes: r.DE, fdr: r.FDR }));

      // A missing file is the pipeline declining to compute that section, not an empty
      // result. The two read differently on screen and must not be collapsed here; the
      // reason itself lives in the run manifest, which this endpoint does not carry.
      const gone = 'not computed for this contrast';
      return {
        genes, dmr: regions, pathways,
        skipped_genes: lit ? undefined : gone,
        skipped_dmr: dmr ? undefined : gone,
        skipped_pathways: go ? undefined : gone,
        volcano_id: `volcano_${contrastId}`,
        pval_id: `pval_dist_${contrastId}`,
      };
    }));
  }

  // GET /aha/v1/omics/{modality}/analysis/charts/{chart_id}?session_id
  getAnalysisChart(chartId: string): Observable<OmicsChart> {
    return this.http.get<OmicsChart>(
      // Encoded: a chart id carries the contrast, and those hold a "+" (`volcano_mci+_vs_nci`).
      `${this.api}/omics/${this.modality}/analysis/charts/${encodeURIComponent(chartId)}`,
      { params: { session_id: this.resolvedSessionId } },
    );
  }

  // GET /aha/v1/omics/{modality}/processing/summary?session_id
  getProcessingSummary(): Observable<ProcessingSummary> {
    return this.http.get<ProcessingSummary>(
      `${this.api}/omics/${this.modality}/processing/summary`,
      { params: { session_id: this.resolvedSessionId } },
    );
  }

  // GET /aha/v1/omics/{modality}/processing/preview?session_id
  getMatrixPreview(): Observable<MatrixPreview> {
    return this.http.get<MatrixPreview>(
      `${this.api}/omics/${this.modality}/processing/preview`,
      { params: { session_id: this.resolvedSessionId } },
    );
  }

  // GET /aha/v1/omics/{modality}/processing/overview?session_id[&probe_from&probe_to
  //   &sample_from&sample_to&rows]
  // Two requests: the dimensions as JSON, and the bytes as a binary body. One byte per value
  // rather than a JSON number — 900 x 736 is 662 KB this way and 4.6 MB the other, for a
  // picture whose colours the frontend chooses anyway.
  getMatrixOverview(q: MatrixOverviewQuery = {}): Observable<MatrixOverview> {
    const params: Record<string, string> = { session_id: this.resolvedSessionId };
    if (q.probe_from  != null) params['probe_from']  = String(q.probe_from);
    if (q.probe_to    != null) params['probe_to']    = String(q.probe_to);
    if (q.sample_from != null) params['sample_from'] = String(q.sample_from);
    if (q.sample_to   != null) params['sample_to']   = String(q.sample_to);
    if (q.rows        != null) params['rows']        = String(q.rows);

    return this.http.get<MatrixOverviewWire>(
      `${this.api}/omics/${this.modality}/processing/overview`, { params },
    ).pipe(map(w => ({
      ...w,
      values: Uint8Array.from(atob(w.values_b64), ch => ch.charCodeAt(0)),
    })));
  }
}
